import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  Dimensions,
  ActivityIndicator,
  SafeAreaView,
} from 'react-native';
import { InsetModal as Modal } from '../components/InsetModal';
import {
    BottomTabs,
    ConfirmationModal,
    Header,
    MoreOptionsModal,
    ScreenLayout,
    StepperReturnBanner,
    colors,
    spacing,
    typography
} from '../components';
import { FacebookPageCard, FacebookPageCardData } from '../components/facebook/FacebookPageCard';
import { FacebookPageSettings, PageSettingsData } from '../components/facebook/FacebookPageSettings';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { Ionicons } from '@expo/vector-icons';
import { apiService } from '../services/api';
import { useApiSync } from '../hooks/useApiSync';
import { shadow } from '../theme/colors';
import { useThemedStyles } from '../hooks/useThemedStyles';

let WebView: any = null;
if (Platform.OS !== 'web') {
    WebView = require('react-native-webview').WebView;
}

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

// Detect if we're on mobile browser (not just web platform)
const isMobileBrowser = Platform.OS === 'web' && (screenWidth < 768 || screenHeight < 768);

export const FacebookMessengerSetupScreen: React.FC = () => {
    const styles = useThemedStyles(getStyles);
    const { currentBusiness } = useAppStore();
    const { showNotification, navigateTo, activeTab, setActiveTab, routeParams } = useAppContext();
    // Embedded inside the channel onboarding stepper, the caller passes where "done" should
    // go next (the next step, not always Home) via routeParams.onCompleteScreen/onCompleteParams.
    const onCompleteScreen = (routeParams as any)?.onCompleteScreen || 'home';
    const onCompleteParams = (routeParams as any)?.onCompleteParams;

    useApiSync();

    // Set active tab to home on mount
    useEffect(() => {
        setActiveTab('home');
    }, [setActiveTab]);

    const [showMoreModal, setShowMoreModal] = useState(false);
    const isMountedRef = useRef(true);

    // Facebook pages from API
    const [facebookPages, setFacebookPages] = useState<Array<{
        id: string;
        page_id: string;
        page_name: string;
        page_access_token: string;
        status: string;
        isVerified: boolean;
        verifiedName?: string;
        verifiedBy?: string;
        is_default?: boolean;
    }>>([]);
    const [isLoadingPages, setIsLoadingPages] = useState(false);
    const [pageLimit, setPageLimit] = useState(1);
    const [canAddMorePages, setCanAddMorePages] = useState(true);

    // Import conversations state
    const [importingPageId, setImportingPageId] = useState<string | null>(null);

    // Sync posts (history backfill) state
    const [syncingPostsPageId, setSyncingPostsPageId] = useState<string | null>(null);

    // Selected page for settings (like WhatsApp phone selection)
    const [selectedPage, setSelectedPage] = useState<FacebookPageCardData | null>(null);

    // Embedded signup: connect a business's own Facebook Page(s) via Meta's popup.
    const [showEmbeddedSignup, setShowEmbeddedSignup] = useState(false);
    const embeddedSignupUrl = `${apiService.getBaseURL()}/public/facebook-messenger-embedded-signup.html?token=${encodeURIComponent(apiService.getAuthToken() || '')}`;

    // Delete/register-pending state
    const [pageToDelete, setPageToDelete] = useState<FacebookPageCardData | null>(null);
    const [isDeletingPage, setIsDeletingPage] = useState(false);
    const [registeringPageId, setRegisteringPageId] = useState<string | null>(null);

    // Per-page real webhook subscription status (distinct from the page's
    // overall verified/connected status) — a page can be fully connected for
    // messaging while its 'feed' field was never actually registered with
    // Meta, silently blocking post/comment/reaction webhooks with no other
    // visible symptom than "nothing ever arrives".
    const [feedSubscriptionByPageId, setFeedSubscriptionByPageId] = useState<Record<string, boolean>>({});

    // Function to fetch Facebook pages from API
    const fetchFacebookPages = async () => {
        try {
            setIsLoadingPages(true);
            const response = await apiService.getFacebookPages();

            if (response.statusCode === 1000 && response.data) {
                setFacebookPages(response.data);
            } else {
                console.log('⚠️ [FacebookMessengerSetup] No Facebook pages found or error:', response);
            }
        } catch (error) {
            console.error('❌ [FacebookMessengerSetup] Error fetching Facebook pages:', error);
        } finally {
            setIsLoadingPages(false);
        }
    };

    const fetchChannelLimits = async () => {
        try {
            const response = await apiService.getFacebookChannelLimits();
            if (response.statusCode === 1000 && response.data) {
                setPageLimit(response.data.maxPages ?? 1);
                setCanAddMorePages((response.data.currentPages ?? 0) < (response.data.maxPages ?? 1));
            }
        } catch (error) {
            console.error('❌ [FacebookMessengerSetup] Error fetching channel limits:', error);
        }
    };

    useEffect(() => {
        fetchFacebookPages();
        fetchChannelLimits();
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Handles both the web `window.postMessage` and native WebView `onMessage` shapes from
    // facebook-messenger-embedded-signup.html — mirrors WhatsAppBusinessSetupScreen's pattern.
    const handleEmbeddedSignupResult = (data: any) => {
        if (data?.type === 'facebook_messenger_embedded_signup_success') {
            setShowEmbeddedSignup(false);
            const connectedPages = data.connectedPages || [];
            const skippedPages = data.skippedPages || [];
            if (connectedPages.length > 0) {
                showNotification(
                    'success',
                    'Facebook Connected',
                    `Connected ${connectedPages.length} page${connectedPages.length > 1 ? 's' : ''}.` +
                    (skippedPages.length > 0 ? ` ${skippedPages.length} skipped (plan limit reached).` : '')
                );
                // Automatic post-history backfill for each newly connected page — silent/
                // background, since the page's feed webhook (subscribed as part of connecting)
                // already covers everything going forward; this just seeds the history once.
                connectedPages.forEach((p: any) => {
                    const connectedPageId = p.page_id || p.id;
                    if (!connectedPageId) return;
                    apiService.importFacebookPosts(connectedPageId).catch((err) => {
                        console.log('⚠️ [FacebookMessengerSetup] Auto post-sync failed for', connectedPageId, err);
                    });
                });
                setTimeout(() => {
                    navigateTo(onCompleteScreen, undefined, onCompleteParams);
                }, 1500);
            } else {
                showNotification(
                    'error',
                    'No Pages Connected',
                    skippedPages[0]?.reason === 'page_limit_reached'
                        ? "You have reached your plan's Facebook page limit."
                        : 'No pages could be connected. Please try again.'
                );
            }
            fetchFacebookPages();
            fetchChannelLimits();
        } else if (data?.type === 'facebook_messenger_embedded_signup_failure') {
            setShowEmbeddedSignup(false);
            showNotification('error', 'Connection Failed', data?.error || 'Failed to connect your Facebook Page.');
        }
    };

    useEffect(() => {
        if (Platform.OS !== 'web' || !showEmbeddedSignup) return;
        const handler = (event: MessageEvent) => handleEmbeddedSignupResult(event.data);
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [showEmbeddedSignup]);

    const handleEmbeddedSignupWebViewMessage = (event: any) => {
        try {
            const data = JSON.parse(event.nativeEvent.data);
            handleEmbeddedSignupResult(data);
        } catch (e) {
            console.error('[FacebookMessengerSetup] Failed to parse embedded signup message:', e);
        }
    };

    // FacebookPageSettings owns the entire save flow itself now (channel-settings doc
    // upsert + flow attachment create/update/delete) — this callback just confirms to the
    // user once that's done, rather than duplicating the upsert here.
    const handlePageSettingsSave = async (settings: PageSettingsData) => {
        showNotification('success', 'Success', 'Page settings saved successfully.');
    };

    // Handle import conversations from Facebook Page
    const handleImportConversations = async (page: FacebookPageCardData) => {
        const pageIdToImport = page.page_id || page.id;
        if (!pageIdToImport) {
            showNotification('error', 'Import Failed', 'Page ID not found');
            return;
        }
        setImportingPageId(pageIdToImport);
        try {
            const response = await apiService.importFacebookPageConversations(pageIdToImport);
            if (response.statusCode === 1000 && response.data) {
                const { imported, totalUniqueContacts, pageName: pn } = response.data;
                showNotification(
                    'success',
                    'Import Complete',
                    `Imported ${imported} contacts from ${pn} (from conversations only). Likes and followers are not imported—only people who have messaged your Page.`
                );
            } else {
                showNotification(
                    'error',
                    'Import Failed',
                    (response.errors as any)?.[0]?.message || 'Failed to import conversations'
                );
            }
        } catch (error: any) {
            showNotification('error', 'Import Failed', error.message || 'Failed to import conversations');
        } finally {
            setImportingPageId(null);
        }
    };

    // Manual one-time backfill of post history from Facebook. New posts made
    // directly on Facebook after this already sync in automatically via the
    // page's feed webhook — this only covers posts that predate that.
    const handleSyncPosts = async (page: FacebookPageCardData) => {
        const pageIdToSync = page.page_id || page.id;
        if (!pageIdToSync) {
            showNotification('error', 'Sync Failed', 'Page ID not found');
            return;
        }
        setSyncingPostsPageId(pageIdToSync);
        try {
            const response = await apiService.importFacebookPosts(pageIdToSync);
            if (response.statusCode === 1000 && response.data) {
                const { imported, total } = response.data;
                showNotification(
                    'success',
                    'Posts Synced',
                    `Imported ${imported} of ${total} post(s) from ${page.page_name}. New posts sync automatically from now on.`
                );
            } else {
                showNotification(
                    'error',
                    'Sync Failed',
                    (response.errors as any)?.[0]?.message || 'Failed to sync posts'
                );
            }
        } catch (error: any) {
            showNotification('error', 'Sync Failed', error.message || 'Failed to sync posts');
        } finally {
            setSyncingPostsPageId(null);
        }
    };

    // Deleting a page lets the business reconnect it (or a different one) via embedded
    // signup again — the main recovery path for a page stuck "Pending" from an
    // incomplete/failed connection.
    const handleDeletePageRequest = (pageId: string) => {
        const page = allPages.find(p => p.page_id === pageId) || null;
        setPageToDelete(page);
    };

    const confirmDeletePage = async () => {
        if (!pageToDelete) return;
        setIsDeletingPage(true);
        try {
            const response = await apiService.deleteFacebookPage(pageToDelete.page_id);
            if (response.statusCode === 1000) {
                showNotification('success', 'Page Removed', 'The Facebook page has been disconnected. You can now reconnect it or add a different one.');
                setPageToDelete(null);
                if (selectedPage?.page_id === pageToDelete.page_id) {
                    setSelectedPage(null);
                }
                await fetchFacebookPages();
                await fetchChannelLimits();
            } else {
                showNotification('error', 'Could Not Remove Page', response.errors?.[0]?.message || 'Please try again.');
            }
        } catch (error: any) {
            showNotification('error', 'Could Not Remove Page', error?.message || 'Please try again.');
        } finally {
            setIsDeletingPage(false);
        }
    };

    // A page connected via embedded signup can be linked to our app but still "Pending" if
    // the webhook subscription step didn't complete — this lets the business finish
    // registering it directly instead of having to delete/reconnect.
    const handleRegisterPendingPage = async (page: FacebookPageCardData) => {
        setRegisteringPageId(page.page_id);
        try {
            const response = await apiService.registerPendingFacebookPage(page.page_id);
            if (response.statusCode === 1000) {
                showNotification('success', 'Page Registered', 'This page is now connected for messaging.');
                await fetchFacebookPages();
            } else {
                showNotification('error', 'Registration Failed', response.errors?.[0]?.message || 'Please try again.');
            }
        } catch (error: any) {
            showNotification('error', 'Registration Failed', error?.message || 'Please try again.');
        } finally {
            setRegisteringPageId(null);
        }
    };

    // Check each connected page's real webhook subscription status once,
    // whenever the page list changes — cheap Graph API GET, not on a poll.
    useEffect(() => {
        const connectedPageIds = facebookPages
            .filter((p) => p.isVerified || p.status === 'verified' || p.status === 'connected')
            .map((p) => p.page_id)
            .filter(Boolean);
        if (connectedPageIds.length === 0) return;

        (async () => {
            const results = await Promise.all(connectedPageIds.map(async (pageId) => {
                try {
                    const res = await apiService.getFacebookWebhookSubscriptionStatus(pageId);
                    const hasFeed = res.statusCode === 1000 && !!res.data?.fields?.includes('feed');
                    return [pageId, hasFeed] as const;
                } catch {
                    return [pageId, true] as const; // fail open — don't show a false warning on a network hiccup
                }
            }));
            setFeedSubscriptionByPageId(Object.fromEntries(results));
        })();
    }, [facebookPages]);

    // Map API pages to card data
    const allPages: FacebookPageCardData[] = facebookPages.map((p) => ({
        id: p.id,
        page_id: p.page_id,
        page_name: p.page_name,
        page_access_token: p.page_access_token,
        status: p.status,
        isVerified: p.isVerified || p.status === 'verified' || p.status === 'connected',
        verifiedBy: p.verifiedBy,
        verifiedName: p.verifiedName,
        is_default: p.is_default,
    }));

    const renderPageStep = () => (
        <ScrollView
            style={styles.container}
            contentContainerStyle={styles.contentContainer}
            showsVerticalScrollIndicator={false}
        >
            {/* Business Display Section - matches WhatsApp layout */}
            <View style={styles.businessSection}>
                <View style={styles.businessHeader}>
                    <Ionicons name="business" size={20} color={colors.primary} />
                    <Text style={styles.businessSectionTitle}>Current Business</Text>
                </View>
                <View style={styles.currentBusiness}>
                    <Text style={styles.businessName}>{currentBusiness?.name}</Text>
                    <Text style={styles.businessRole}>Owner</Text>
                </View>
            </View>

            {/* Registered Pages Section - matches WhatsApp Registered Phone Numbers */}
            <View style={styles.pagesSection}>
                <View style={styles.sectionHeader}>
                    <View>
                        <Text style={styles.sectionTitle}>Registered Facebook Pages</Text>
                        <Text style={styles.sectionSubtitle}>Import adds contacts from conversations only (people who messaged your Page). Likes and followers are not imported.</Text>
                    </View>
                </View>

                {canAddMorePages && (
                    <TouchableOpacity
                        style={styles.connectFacebookButton}
                        onPress={() => setShowEmbeddedSignup(true)}
                        activeOpacity={0.7}
                    >
                        <Ionicons name="logo-facebook" size={20} color={colors.white} />
                        <Text style={styles.connectFacebookButtonText}>
                            {allPages.length > 0 ? 'Connect More Pages' : 'Connect with Facebook'}
                        </Text>
                    </TouchableOpacity>
                )}

                {isLoadingPages ? (
                    <View style={styles.loadingContainer}>
                        <ActivityIndicator size="small" color={colors.primary} />
                        <Text style={styles.loadingText}>Loading Facebook pages...</Text>
                    </View>
                ) : allPages.length > 0 ? (
                    <>
                        {allPages.map((page) => (
                            <FacebookPageCard
                                key={page.page_id || page.id}
                                page={page}
                                onPress={setSelectedPage}
                                onImportConversations={handleImportConversations}
                                isImporting={importingPageId === (page.page_id || page.id)}
                                onSyncPosts={handleSyncPosts}
                                isSyncingPosts={syncingPostsPageId === (page.page_id || page.id)}
                                onDelete={handleDeletePageRequest}
                                onRegister={handleRegisterPendingPage}
                                isRegistering={registeringPageId === page.page_id}
                                showActions={true}
                                feedNotSubscribed={feedSubscriptionByPageId[page.page_id] === false}
                            />
                        ))}
                    </>
                ) : (
                    <View style={styles.emptyPagesContainer}>
                        <Ionicons name="logo-facebook" size={48} color={colors.text.secondary} />
                        <Text style={styles.emptyPagesText}>No Facebook pages connected</Text>
                        <Text style={styles.emptyPagesSubtext}>
                            Connect your first Facebook page to get started with Messenger
                        </Text>
                    </View>
                )}

                {!canAddMorePages && (
                    <View style={styles.limitReachedSection}>
                        <Ionicons name="warning-outline" size={20} color={colors.warning} />
                        <Text style={styles.limitReachedText}>
                            You have reached the maximum number of Facebook pages ({pageLimit}) allowed by your subscription plan.
                        </Text>
                    </View>
                )}
            </View>

            {/* Page settings: how the system behaves when receiving messages (like WhatsApp phone settings) */}
            {selectedPage && (
                <View style={styles.settingsSection}>
                    <FacebookPageSettings
                        page={selectedPage}
                        onSave={handlePageSettingsSave}
                    />
                </View>
            )}

            {!selectedPage && allPages.length > 0 && (
                <View style={styles.noSelectionContainer}>
                    <Ionicons name="settings-outline" size={48} color={colors.text.secondary} />
                    <Text style={styles.noSelectionText}>Select a page to configure how the system behaves when receiving messages</Text>
                </View>
            )}
        </ScrollView>
    );

    const handleTabPress = createTabPressHandler(navigateTo, setActiveTab, { onMore: () => setShowMoreModal(true) });

    // Right pane content (web) - placeholder, page settings render inline in renderPageStep
    const renderRightPaneContent = () => (
        <View style={styles.rightPanePlaceholder}>
            <Ionicons name="logo-facebook" size={64} color={colors.text.secondary} />
            <Text style={styles.rightPanePlaceholderText}>
                {allPages.length > 0
                    ? 'Use "Import" to sync contacts from people who have messaged your Page (conversations). Likes and followers cannot be imported.'
                    : 'Connect your first Facebook page to get started'}
            </Text>
        </View>
    );

    return (
        <ScreenLayout
            header={
                <Header
                    showNotifications={false}
                    showMessages={false}
                    showNavigation={Platform.OS === 'web'}
                    activeTab={activeTab}
                    onTabPress={handleTabPress}
                />
            }
            showBottomTabs={Platform.OS !== 'web'}
            onLeftPaneClose={() => {}}
            leftPaneContent="settings"
            onSettingsItemPress={async (itemId) => {
                const { setPendingSettingsItem } = await import('../utils/tempStorage');
                await setPendingSettingsItem(itemId);
                navigateTo('settings');
            }}
            rightPaneContent="custom"
            rightPaneCustomContent={Platform.OS === 'web' ? renderRightPaneContent() : null}
            onRightPaneClose={() => {}}
        >
            <StepperReturnBanner />
            {renderPageStep()}


            <MoreOptionsModal visible={showMoreModal} onClose={() => setShowMoreModal(false)} />

            {showEmbeddedSignup && (
                <Modal visible={showEmbeddedSignup} animationType="slide" onRequestClose={() => setShowEmbeddedSignup(false)}>
                    <SafeAreaView style={styles.embeddedSignupContainer}>
                        <View style={styles.embeddedSignupHeader}>
                            <Text style={styles.embeddedSignupTitle}>Connect Facebook Messenger</Text>
                            <TouchableOpacity onPress={() => setShowEmbeddedSignup(false)}>
                                <Ionicons name="close" size={26} color={colors.text.secondary} />
                            </TouchableOpacity>
                        </View>
                        {Platform.OS === 'web' ? (
                            <iframe
                                src={embeddedSignupUrl}
                                style={{ flex: 1, border: 'none', width: '100%', height: '100%' }}
                                title="Connect Facebook Messenger"
                            />
                        ) : WebView ? (
                            <WebView
                                source={{ uri: embeddedSignupUrl }}
                                onMessage={handleEmbeddedSignupWebViewMessage}
                                startInLoadingState
                                renderLoading={() => (
                                    <View style={styles.loadingContainer}>
                                        <ActivityIndicator size="large" color={colors.primary} />
                                        <Text style={styles.loadingText}>Loading...</Text>
                                    </View>
                                )}
                            />
                        ) : null}
                    </SafeAreaView>
                </Modal>
            )}

            <ConfirmationModal
                visible={!!pageToDelete}
                onClose={() => setPageToDelete(null)}
                onConfirm={confirmDeletePage}
                title="Remove Facebook Page"
                message={`Are you sure you want to disconnect ${pageToDelete?.page_name || 'this page'}? You can reconnect it or add a different one afterwards.`}
                confirmText="Remove"
                cancelText="Cancel"
                type="error"
                loading={isDeletingPage}
            />
        </ScreenLayout>
    );
};

const getStyles = () => StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background.primary,
    },
    contentContainer: {
        flexGrow: 1,
        paddingHorizontal: isMobileBrowser ? spacing.screenPadding : (Platform.OS === 'web' ? spacing.lg : spacing.screenPadding),
        paddingVertical: spacing.lg,
        paddingBottom: Platform.OS === 'web' ? spacing.lg : 100,
        ...(Platform.OS === 'web' && !isMobileBrowser && { maxWidth: '100%', width: '100%' }),
    },
    // Business section styles (matches WhatsApp layout)
    businessSection: {
        backgroundColor: colors.background.surface,
        borderRadius: spacing.borderRadius?.md || 8,
        padding: spacing.md,
        marginBottom: spacing.lg,
        borderWidth: 1,
        borderColor: colors.border,
    },
    businessHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
        marginBottom: spacing.sm,
    },
    businessSectionTitle: {
        fontSize: typography.fontSize.sm,
        fontFamily: typography.fontFamily?.medium || undefined,
        color: colors.text.secondary || colors.textLight,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    currentBusiness: {
        gap: spacing.xs,
    },
    businessName: {
        fontSize: typography.fontSize.base,
        fontWeight: typography.fontWeight.semibold as any,
        color: colors.textDark,
        marginBottom: spacing.xs,
    },
    businessRole: {
        fontSize: typography.fontSize.sm,
        color: colors.textLight,
    },
    pagesSection: {
        marginBottom: spacing.xl,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: spacing.md,
    },
    sectionTitle: {
        fontSize: typography.fontSize.lg,
        fontFamily: typography.fontFamily?.semibold || undefined,
        color: colors.text?.primary || colors.textDark,
    },
    sectionSubtitle: {
        fontSize: typography.fontSize.xs,
        color: colors.text?.secondary || colors.textLight,
        marginTop: spacing.xs,
        maxWidth: 320,
    },
    connectFacebookButton: {
        backgroundColor: '#1877F2',
        paddingHorizontal: spacing.lg,
        paddingVertical: spacing.md,
        borderRadius: spacing.sm,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: spacing.sm,
        marginBottom: spacing.md,
    },
    connectFacebookButtonText: {
        color: 'white',
        fontSize: typography.fontSize.base,
        fontWeight: typography.fontWeight.medium as any,
    },
    emptyPagesContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: spacing.xl * 2,
        paddingHorizontal: spacing.lg,
    },
    emptyPagesText: {
        fontSize: typography.fontSize.base,
        fontFamily: typography.fontFamily?.semibold || undefined,
        color: colors.text?.primary || colors.textDark,
        marginTop: spacing.md,
        marginBottom: spacing.xs,
    },
    emptyPagesSubtext: {
        fontSize: typography.fontSize.sm,
        color: colors.text?.secondary || colors.textLight,
        textAlign: 'center',
    },
    rightPanePlaceholder: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    rightPanePlaceholderText: {
        color: colors.text?.secondary || colors.textLight,
        fontSize: 16,
        marginTop: 16,
        textAlign: 'center',
    },
    limitReachedSection: {
        width: '100%',
        padding: spacing.md,
        backgroundColor: colors.background.secondary,
        borderRadius: spacing.sm,
        borderWidth: 1,
        borderColor: colors.border,
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: spacing.sm,
    },
    limitReachedText: {
        fontSize: typography.fontSize.sm,
        color: colors.textLight,
        textAlign: 'center',
        lineHeight: 18,
    },
    settingsSection: {
        marginTop: spacing.lg,
    },
    noSelectionContainer: {
        marginTop: spacing.xl,
        padding: spacing.xl,
        alignItems: 'center',
        justifyContent: 'center',
    },
    noSelectionText: {
        fontSize: typography.fontSize.base,
        color: colors.text.secondary,
        marginTop: spacing.md,
        textAlign: 'center',
    },
    loadingContainer: {
        padding: spacing.md,
        alignItems: 'center',
    },
    loadingText: {
        fontSize: typography.fontSize.sm,
        color: colors.textLight,
    },
    embeddedSignupContainer: {
        flex: 1,
        backgroundColor: colors.background.surface,
    },
    embeddedSignupHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.sm,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    embeddedSignupTitle: {
        fontSize: typography.fontSize.lg,
        fontWeight: typography.fontWeight.bold as any,
        color: colors.textDark,
    },
});
